import styled from "styled-components";
import BaseLayout from "common/Layouts/Layout.js";
import ChartTimeRangeButtons from "Global/ChartTimeRangeButtons";
import { useDispatch } from "react-redux";
import useMeasure from "utils/hooks/useMeasure";
import Divider from "antd/es/divider";
import Flex from "common/Boxes/Flex";
import React from "react";
import useMedia from "utils/hooks/useMedia";

const TABS_AREA = "tabs";
const CHART_AREA = "chart";
const TIME_RANGE_FILTERS_AREA = "timeRangeFilters";
const CHANGE_AREA = "change";
const MIN_MAX_AREA = "minMax";
const SELECTION_DROPDOWNS_AREA = "selectionDropdowns";

const MOBILE_WIDTH_BREAKPOINT = 550;

const ChartCardLayout = ({
  tabs,
  chart,
  change,
  minMax,
  selectedTimeRange,
  filtersActions,
  timeRanges,
  buttonSize,
  additionalButtons,
  selectionDropdowns,
  mobileBreakpoint,
  autoLayoutHeight,
  setTimeRange,
  style,
  hideTimeRanges,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMedia([`(max-width: 500px)`], [true], false);
  const [measureLayoutRef, { width: layoutWidth }] = useMeasure();

  const checkUseMobileBreakpoint = () => {
    if (mobileBreakpoint === Infinity) return true;
    let breakpoint = mobileBreakpoint || MOBILE_WIDTH_BREAKPOINT;
    if (layoutWidth < breakpoint) return true;
    return selectionDropdowns && layoutWidth < 950;
  };

  const useMobileLayout = checkUseMobileBreakpoint();

  return (
    <Layout
      ref={measureLayoutRef}
      $isMobile={isMobile}
      $showChange={!!change}
      $showTabs={!!tabs}
      $showMinMax={!!minMax}
      $showSelectionDropdowns={!!selectionDropdowns}
      $useMobileLayout={useMobileLayout}
      $autoLayoutHeight={autoLayoutHeight}
      style={style}
    >
      {tabs && <TabsItem>{tabs}</TabsItem>}
      <GridItem $gridArea={CHART_AREA}>
        <InnerGridItem>{chart}</InnerGridItem>
      </GridItem>
      {!hideTimeRanges && (
        <TimeRangeFiltersGridItem
          style={{ display: "flex" }}
          $useMobileLayout={useMobileLayout}
        >
          <ChartTimeRangeButtons
            timeRanges={timeRanges}
            selectedTimeRange={selectedTimeRange}
            handleSelectTimeRange={(timeRange) => {
              if (setTimeRange) setTimeRange(timeRange);
              else dispatch(filtersActions.setTimeRangeFilter(timeRange));
            }}
            buttonSize={buttonSize}
            additionalButtons={additionalButtons}
          />
          {!useMobileLayout && selectionDropdowns && (
            <>
              <Divider
                orientation="vertical"
                style={{ margin: "0 8px 0 4px", height: "100%" }}
              />
              <Flex>{selectionDropdowns}</Flex>
            </>
          )}
        </TimeRangeFiltersGridItem>
      )}
      {change && <GridItem $gridArea={CHANGE_AREA}>{change}</GridItem>}
      {minMax && <GridItem $gridArea={MIN_MAX_AREA}>{minMax}</GridItem>}
      {useMobileLayout && selectionDropdowns && (
        <SelectionDropdownGridItem>
          {selectionDropdowns}
        </SelectionDropdownGridItem>
      )}
    </Layout>
  );
};

export default ChartCardLayout;

const getChartMinHeight = (props) => {
  if (!props.$autoLayoutHeight) {
    return props.$isMobile ? "200px" : "300px";
  }
  return "minmax(0, 1fr)";
};

const Layout = styled(BaseLayout)`
  padding: 0;
  grid-template-areas:
    ${(p) =>
      p.$showChange
        ? `
      "${TABS_AREA} ${TABS_AREA}"
      "${CHANGE_AREA} ${TIME_RANGE_FILTERS_AREA}"
    `
        : `
      "${TABS_AREA} ${TIME_RANGE_FILTERS_AREA}"
    `}
    ${(p) => (p.$showMinMax ? `"${MIN_MAX_AREA} ${MIN_MAX_AREA}"` : "")}
    "${CHART_AREA} ${CHART_AREA}";
  grid-template-rows: ${(p) => (p.$showChange ? "auto" : "")} ${(p) =>
      p.$showMinMax ? "auto" : ""} auto ${(p) => getChartMinHeight(p)};
  grid-template-columns: auto 1fr;
  ${(p) =>
    p.$useMobileLayout &&
    `
    grid-template-areas:
      ${p.$showTabs ? `"${TABS_AREA}"` : ""}
      "${CHANGE_AREA}"
      ${p.$showMinMax ? `"${MIN_MAX_AREA}"` : ""}
      ${p.$showSelectionDropdowns ? `"${SELECTION_DROPDOWNS_AREA}"` : ""}
      "${CHART_AREA}"
      "${TIME_RANGE_FILTERS_AREA}";
    grid-template-rows: ${p.$showTabs ? "auto " : ""} auto ${
      p.$showMinMax ? "auto " : ""
    } ${p.$showSelectionDropdowns ? "auto " : ""} ${getChartMinHeight(p)} auto;
    grid-template-columns: minmax(0, 1fr);
    row-gap: 8px;
  `}
`;
const GridItem = styled.div`
  display: grid;
  ${(p) =>
    p.$gridArea &&
    `
    grid-area: ${p.$gridArea};
  `}
`;
const TimeRangeFiltersGridItem = styled(GridItem)`
  grid-area: ${TIME_RANGE_FILTERS_AREA};
  justify-self: end;
  ${(p) =>
    p.$useMobileLayout &&
    `
    justify-self: start;
  `};
`;
const SelectionDropdownGridItem = styled(GridItem)`
  grid-area: ${SELECTION_DROPDOWNS_AREA};
  justify-self: end;
  display: flex;
`;
const TabsItem = styled(GridItem)`
  grid-area: ${TABS_AREA};
  justify-self: start;
`;
const InnerGridItem = styled.div`
  height: 100%;
  width: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
`;
